/* eslint-disable no-console */
type SnackbarNotifier = (message: string) => void;

let snackbarNotifier: SnackbarNotifier | null = null;

export const registerSnackbarNotifier = (notifier: SnackbarNotifier | null): void => {
  snackbarNotifier = notifier;
};

const DEFAULT_MESSAGE = 'Something went wrong. Please try again.';

export const getErrorMessage = (err: unknown): string => {
  const axiosErr = err as {
    response?: { status?: number };
    message?: string;
    code?: string;
  };

  const status = axiosErr?.response?.status;
  if (status === 400) return 'Invalid request. Please check your input and try again.';
  if (status === 401 || status === 403) return 'Please sign in to continue.';
  if (status === 404) return 'The requested item was not found.';
  if (status === 408) return 'The request timed out. Please try again.';
  if (status && status >= 500) return 'Something went wrong. Please try again later.';

  const message = (err instanceof Error ? err.message : axiosErr?.message ?? (typeof err === 'string' ? err : '')).toLowerCase();
  const code = (err as { code?: string })?.code?.toLowerCase() ?? '';

  if (/network error|failed to fetch|err_network/.test(message) || code === 'err_network') {
    return 'Unable to connect. Please check your connection.';
  }
  if (/timeout|etimedout/.test(message) || code === 'etimedout') {
    return 'The request timed out. Please try again.';
  }
  if (/econnrefused/.test(message) || code === 'econnrefused') {
    return 'Unable to reach the server. Please try again later.';
  }

  return DEFAULT_MESSAGE;
};

export interface HandleServiceErrorOptions {
  customMessage?: string;
  showSnackbar?: boolean;
}

export const handleServiceError = (
  err: unknown,
  options?: HandleServiceErrorOptions
): void => {
  console.error(err);

  if (typeof window === 'undefined') return;
  if (options?.showSnackbar === false) return;
  if (!snackbarNotifier) return;

  const message = options?.customMessage ?? getErrorMessage(err);
  snackbarNotifier(message);
};

export interface WithServiceErrorOptions<T> {
  fallback?: T;
  getMessage?: (err: unknown) => string;
}

export const withServiceError = <T, A extends unknown[]>(
  fn: (...args: A) => Promise<T>,
  options?: WithServiceErrorOptions<T>
): ((...args: A) => Promise<T | null>) => async (...args: A): Promise<T | null> => {
    try {
      return await fn(...args);
    } catch (err) {
      handleServiceError(err, {
        customMessage: options?.getMessage?.(err)
      });

      return options?.fallback ?? null;
    }
  };
